import type { ComponentPropsWithRef, JSX, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Кнопки панели. Иконочные — отдельным компонентом `IconButton`, у которого
 * `aria-label` обязателен по типам (docs/10 §6, чеклист §9).
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover',
  secondary: 'border border-border-strong bg-surface-2 text-fg hover:bg-surface-3',
  ghost: 'text-fg-muted hover:bg-surface-3 hover:text-fg',
  danger: 'bg-danger text-on-danger hover:opacity-90'
}

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: 'h-8 gap-2 px-3 text-xs',
  md: 'h-10 gap-2 px-4 text-base'
}

const BASE_CLASS =
  'inline-flex items-center justify-center rounded-md font-medium transition-[background-color,opacity] duration-150 ' +
  'disabled:cursor-not-allowed disabled:opacity-50'

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={cn(BASE_CLASS, VARIANT_CLASS[variant], SIZE_CLASS[size], className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : null}
      {children}
    </button>
  )
}

export interface IconButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** Обязателен: иконка без текста нечитаема скринридером. */
  'aria-label': string
  children: ReactNode
}

export function IconButton({
  variant = 'ghost',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: IconButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={cn(
        BASE_CLASS,
        VARIANT_CLASS[variant],
        size === 'sm' ? 'size-8' : 'size-10',
        className
      )}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : children}
    </button>
  )
}
